<template>
  <div>
    <v-dialog v-model="open" width="500">
      <template v-slot:activator="{ on, attrs }">
        <v-btn small outlined color="primary" v-bind="attrs" v-on="on" block :disabled="disabled">
          Adicionar local
        </v-btn>
      </template>

      <v-card>
        <v-toolbar elevation="0">
          <v-card-title class="ml-0 pl-1">
            Adicionar local
          </v-card-title>

          <v-spacer />

          <v-btn icon @click="open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          Deseja realmente adicionar local para a conta selecionada? Verifique todos os dados antes de continuar
        </v-card-text>

        <v-card-text>
          <v-form ref="form" v-model="formValid" lazy-validation @submit="e => e.preventDefault()">
            <v-text-field label="Nome do local" v-model="formData.location_name" :rules="locationNameRule" required
              outlined></v-text-field>

            <v-text-field label="Código do local" v-model="formData.store_code" :rules="locationStoreCodeRule" required
              outlined></v-text-field>

            <v-autocomplete v-model="formData.primary_category" :search-input.sync="searchCategory"
              :loading="loadingCategories" :items="categories.data" :rules="locationPrimaryCategoryRule"
              item-text="display_name" item-value="category_id" menu-props="auto" label="Categoria principal"
              outlined></v-autocomplete>

            <my-autocomplete class="mb-6" style="width: 100%" label="Endereço" :value="formData.address.address_line_one"
              :callback="callbackGeocode" @click="item => changeGeocodeAddress(item)"
              @change="val => formData.address.address_line_one = val" :rules="locationAddressRule" />

            <v-text-field label="Complemento" v-model="formData.address.address_line_two" required
              outlined></v-text-field>

            <v-text-field label="Bairro" v-model="formData.address.sublocality" :rules="locationSublocalityRule" required
              outlined></v-text-field>

            <v-text-field label="Cidade" v-model="formData.address.locality" :rules="locationLocalityRule" required
              outlined></v-text-field>

            <v-text-field label="Estado" v-model="formData.address.administrative_area"
              :rules="locationAdministrativeAreaRule" required outlined></v-text-field>

            <v-text-field label="Cep" v-mask="'#####-###'" v-model="formData.address.postal_code"
              :rules="locationPostalCodeRule" required outlined></v-text-field>

            <v-divider />

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="secondary" text @click="cancel">
                Cancelar
              </v-btn>
              <v-btn :loading="loading" color="primary" text @click="callAction">
                Aplicar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { handleResponseError } from '@/api/handleError'
import AutoComplete from '@/components/AutoComplete';
import { debounce } from "lodash";

export default {
  name: 'LocationStoreModal',
  computed: {
    ...mapState({
      categories: state => state.locations.categories
    }),
  },
  props: {
    action: Function,
    accountName: String,
    onUpdated: Function,
    disabled: Boolean
  },
  components: {
    'my-autocomplete': AutoComplete
  },
  created() {
    this.fetchCategories()
  },
  data: () => ({
    snackbar: {
      text: '',
      show: false,
      color: 'info',
    },
    open: false,
    formValid: true,
    loading: false,
    loadingCategories: false,
    searchCategory: '',
    formData: {
      location_name: '',
      store_code: '',
      primary_category: '',
      address: {
        address_line_one: '',
        address_line_two: '',
        administrative_area: '',
        locality: '',
        postal_code: '',
        sublocality: ''
      },
      latitude: 0,
      longitude: 0
    },
    locationNameRule: [(v) => !!v || 'O nome da empresa é obrigatório'],
    locationStoreCodeRule: [(v) => !!v || 'O código da empresa é obrigatório'],
    locationPrimaryCategoryRule: [(v) => !!v || 'A categoria principal da empresa é obrigatório'],
    locationAddressRule: [(v) => !!v || 'O endereço principal da empresa é obrigatório'],
    locationSublocalityRule: [(v) => !!v || 'O bairro da empresa é obrigatório'],
    locationLocalityRule: [(v) => !!v || 'A cidade da empresa é obrigatório'],
    locationAdministrativeAreaRule: [(v) => !!v || 'O estado da empresa é obrigatório'],
    locationPostalCodeRule: [(v) => !!v || 'O código postal da empresa é obrigatório'],
  }),
  methods: {
    cancel: function () {
      this.open = false
      this.resetData()
    },
    resetData: function () {
      this.formData.location_name = ''
      this.formData.store_code = ''
      this.formData.primary_category = ''
      this.formData.address.address_line_one = ''
      this.formData.address.address_line_two = ''
      this.formData.address.administrative_area = ''
      this.formData.address.locality = ''
      this.formData.address.postal_code = ''
      this.formData.address.sublocality = ''
    },
    validate: function () {
      return this.$refs.form.validate();
    },
    changeGeocodeAddress(value) {
      this.updateMap(value.lat, value.lng, value.address)
    },
    async callbackGeocode(search) {
      const result = await this.$store.dispatch('locations/geocodeSearch', {
        key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
        address: search
      })
      return result.map(item => ({ title: item.full_text, value: item.title, ...item }))
    },
    updateMap(latitude, longitude, address) {
      this.setLatLng(latitude, longitude);
      this.formData.address = { ...this.formData.address, ...address };
    },
    setLatLng(lat, lng) {
      if (typeof lat === 'number' && typeof lng === 'number') {
        this.formData.latitude = lat;
        this.formData.longitude = lng;
      }
    },
    async fetchCategories(search) {
      this.loadingCategories = true
      await this.$store.dispatch('locations/getCategories', {
        limit: 10,
        search: search,
      }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar as categorias.')
        this.showNotification(message, 'red darken-2')
        this.loadingCategories = false
      })
      this.loadingCategories = false
    },
    onCategoryKeyUp: debounce(function (search) {
      this.fetchCategories(search)
    }, 500),
    callAction: async function () {
      try {
        if (!this.$refs.form.validate()) {
          return false
        }

        this.formData.account_name = this.accountName

        this.loading = true
        await this.$store.dispatch('locations/create', this.formData)
          .then(() => {
            this.showNotification('Local adicionado com sucesso!', 'green darken-2')
            if (this.onUpdated) this.onUpdated()
            this.loading = false
            this.open = false
          })
          .catch(e => {
            const message = handleResponseError(e, 'Ocorreu um erro ao enviar os dados.')
            this.showNotification(message, 'red darken-2')
          })
      } catch (e) {
        const message = handleResponseError(e, 'Ocorreu um erro ao enviar os dados.')
        this.showNotification(message, 'red darken-2')
      }

      this.loading = false
    },
    showNotification: function (message, color) {
      this.snackbar.text = message
      this.snackbar.show = true
      this.snackbar.color = color
    }
  },
  watch: {
    searchCategory(search) {
      if (search) {
        this.onCategoryKeyUp(search)
      }
    },
  }
}
</script>

<style></style>
